import { useCallback, useMemo, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import ChatMessageInput from 'tripPlanner/components/Chat/ChatMessageInput'
import { CHAT_INPUT_OUTER_RADIUS } from 'tripPlanner/components/Chat/ChatInputShell'
import { useChatFiles } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatFilesContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useTextDirection from 'hooks/useTextDirection'
import noop from 'lib/function/noop'
import Group from 'components/utils/Group'
import HideVisually from 'components/Common/HideVisually'
import ChatLandingPlaceholderTicker from './ChatLandingPlaceholderTicker'
import landingComposerMessages from './landingComposerMessages'
import { useRotatingPlaceholder } from './useRotatingPlaceholder'
import TripDetailsNudge from './TripDetailsNudge'
import { TRIP_DETAIL_QUESTION_MESSAGES } from './tripDetailsNudgeMessages'
import { useTripDetailsNudge } from './useTripDetailsNudge'
import { useCaretAnchor } from './useCaretAnchor'

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 ${rem(10)} 0 var(--glow-colour);
  }
  50% {
    box-shadow: 0 0 ${rem(40)} ${rem(15)} var(--glow-colour);
  }
`

const GlowingInputWrapper = styled.div`
  --glow-colour: ${props => props.theme.palette.highlight.primary.lightForeground};

  inline-size: 100%;
  border-radius: ${rem(CHAT_INPUT_OUTER_RADIUS)};
  animation: ${glow} 4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

interface Props {
  onSubmit: (message: string) => void
  disabled?: boolean
  selectedTags: Array<string>
  onRemoveTag?: (tag: string) => void
}

function ChatLandingComposer({
  onSubmit,
  disabled = false,
  selectedTags,
  onRemoveTag = noop,
}: Props) {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const { files, setFiles } = useChatFiles()
  const [message, setMessage] = useState('')
  const [isVoiceBusy, setIsVoiceBusy] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [composerElement, setComposerElement] = useState<HTMLDivElement | null>(null)
  const [composerContent, setComposerContent] = useState<HTMLDivElement | null>(null)
  const textDirection = useTextDirection(wrapperRef)
  const hasTags = selectedTags.length > 0
  const { nudge, announcedDetail, isAnswered, markShown, answer, dismiss, trackSubmitted } = useTripDetailsNudge({
    message,
    selectedTags,
    isVoiceBusy,
    disabled,
    onMessageChange: setMessage,
  })
  const caretAnchor = useCaretAnchor(inputRef, message, { frame: composerContent, isWhole: isAnswered })

  const handleSubmit = useCallback((submitted: string) => {
    trackSubmitted()
    onSubmit(submitted)
  }, [onSubmit, trackSubmitted])

  const nudgeAnnouncement = announcedDetail ? intl.formatMessage(TRIP_DETAIL_QUESTION_MESSAGES[announcedDetail]) : ''

  const placeholderExamples = useMemo((): readonly [string, ...Array<string>] => [
    intl.formatMessage({
      id: 'bpGHvxQ-jpASIV87mlD8',
      defaultMessage: '“Help me plan a honeymoon in Japan”',
      description: 'Rotating example prompt for a honeymoon in Japan, in quotes after "Try:" in the empty TripPlanner landing chat input',
    }),
    intl.formatMessage({
      id: 'iF3vdnJplOFOr4qRYDf8',
      defaultMessage: '“A week in Bali for two in May”',
      description: 'Rotating example prompt for a couples trip to Bali, in quotes after "Try:" in the empty TripPlanner landing chat input',
    }),
    intl.formatMessage({
      id: 'vYl_tWonkirFSq_wGKuG',
      defaultMessage: '“Family resort in Fiji with a kids club”',
      description: 'Rotating example prompt for a family resort in Fiji, in quotes after "Try:" in the empty TripPlanner landing chat input',
    }),
  ], [intl])

  const { item: example, nextItem: nextExample, isRotating } = useRotatingPlaceholder(
    placeholderExamples,
    hasTags || message.trim().length > 0,
  )

  const examplePlaceholder = intl.formatMessage(landingComposerMessages.tryExample, { example })

  // With tags selected the input falls back to its own prompt for adding to them.
  const showsExamples = !hasTags

  const placeholderOverlay = useMemo(() => {
    if (!showsExamples) return undefined
    return <ChatLandingPlaceholderTicker example={example} nextExample={nextExample} isRotating={isRotating} />
  }, [example, isRotating, nextExample, showsExamples])

  return (
    <Group ref={wrapperRef} direction="vertical" fullWidth>
      <GlowingInputWrapper ref={setComposerElement}>
        <ChatMessageInput
          onSubmit={handleSubmit}
          disabled={disabled}
          value={message}
          onChange={setMessage}
          placeholder={showsExamples ? examplePlaceholder : undefined}
          placeholderOverlay={placeholderOverlay}
          files={files}
          onFilesChange={setFiles}
          selectedTags={selectedTags}
          onRemoveTag={onRemoveTag}
          autoFocus={!isMobile}
          dismissKeyboardOnSubmit={isMobile}
          hideDisclaimer
          voicePrompt="landingTip"
          layout="stacked"
          // Bookings are added from the landing's upcoming trips, so attaching opens the file picker straight away.
          attachmentMode="picker"
          onVoiceBusyChange={setIsVoiceBusy}
          inputRef={inputRef}
          contentRef={setComposerContent}
        />
      </GlowingInputWrapper>
      {nudge && (
        <TripDetailsNudge
          anchor={caretAnchor}
          boundary={composerElement ?? undefined}
          textDirection={textDirection}
          detail={nudge.detail}
          showsAnswers={nudge.showsAnswers}
          returnFocusRef={inputRef}
          onShown={markShown}
          onAnswer={answer}
          onDismiss={dismiss}
        />
      )}
      <HideVisually aria-live="polite" aria-atomic="true">
        {nudgeAnnouncement}
      </HideVisually>
    </Group>
  )
}

export default ChatLandingComposer
